import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/utils/axiosConfig';
import { getApiUrl } from '@/utils/apiUrl';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, RefreshCw, FolderOpen, Mail, Paperclip, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = getApiUrl();
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const SummaryText = ({ text }) => (
  <div className="text-sm text-gray-700 leading-relaxed space-y-1 break-words" data-testid="thread-summary-text">
    {text.split('\n').map((line, i) => {
      if (!line.trim()) return <div key={`blank-${i}`} className="h-1.5" />;
      const parts = line.split('**');
      return <p key={`${i}-${line.slice(0, 24)}`}>{parts.map((p, j) => (j % 2 ? <strong key={`${j}-${p.slice(0, 16)}`} className="text-gray-900">{p}</strong> : p))}</p>;
    })}
  </div>
);

const ThreadMessage = ({ m, highlight }) => {
  const [open, setOpen] = useState(highlight);
  const sent = m.direction === 'sent';
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-white'}`}
      data-testid={`thread-message-${m.id}`}>
      <button type="button" className="w-full text-left flex items-start gap-3" onClick={() => setOpen((o) => !o)}>
        <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${sent ? 'bg-blue-100' : 'bg-amber-100'}`}>
          <Mail className={`w-3.5 h-3.5 ${sent ? 'text-blue-600' : 'text-amber-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 truncate">{m.from_name || m.from_addr}</span>
            <Badge className={`text-[9px] px-1 py-0 ${sent ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'}`}>{sent ? 'Envoyé' : 'Reçu'}</Badge>
            <span className="text-xs text-gray-400">{fmt(m.date)}</span>
          </div>
          <p className="text-xs text-gray-500 truncate">{m.subject || '(sans objet)'} · à {m.to}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="mt-2 ml-10 text-xs text-gray-600 whitespace-pre-wrap break-words">
          {m.body_text || <span className="italic text-gray-400">Corps du message vide</span>}
          {m.attachments?.length > 0 && (
            <p className="mt-2 flex items-center gap-1 text-red-500"><Paperclip className="w-3 h-3" /> {m.attachments.join(', ')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export const EmailThreadDialog = ({ emailId, open, onOpenChange }) => {
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summarizing, setSummarizing] = useState(false);

  const summarize = useCallback(async (force = false) => {
    setSummarizing(true);
    try {
      const res = await axios.post(`${API}/emails/${emailId}/thread-summary`, { force });
      setThread((t) => (t ? { ...t, summary: res.data } : t));
    } catch (err) {
      toast.error(err?.data?.detail || 'Résumé IA indisponible');
    } finally {
      setSummarizing(false);
    }
  }, [emailId]);

  useEffect(() => {
    if (!open || !emailId) return;
    let cancelled = false;
    setThread(null);
    setLoading(true);
    axios.get(`${API}/emails/${emailId}/thread`)
      .then((res) => {
        if (cancelled) return;
        setThread(res.data);
        if (!res.data.summary || res.data.summary.stale) summarize(false);
      })
      .catch((err) => toast.error(err?.data?.detail || 'Fil introuvable'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, emailId, summarize]);

  const summary = thread?.summary;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="email-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="w-5 h-5 text-red-600" /> Fil de conversation
            {thread && <Badge variant="outline" className="text-xs" data-testid="thread-message-count">{thread.messages.length} message{thread.messages.length > 1 ? 's' : ''}</Badge>}
          </DialogTitle>
          <DialogDescription className="truncate">
            {thread ? `${thread.subject}${thread.client_name ? ` · ${thread.client_name}` : ''}` : 'Chargement du fil…'}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>}

        {thread && (
          <div className="space-y-4 min-w-0 overflow-hidden">
            <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4" data-testid="thread-summary-card">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="font-bold text-sm flex items-center gap-2 text-violet-900"><Sparkles className="w-4 h-4" /> Résumé IA du fil complet</h3>
                <div className="flex items-center gap-2">
                  {thread.file && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="thread-open-file-btn"
                      onClick={() => { onOpenChange(false); navigate(`/files/${thread.file.id}`); }}>
                      <FolderOpen className="w-3.5 h-3.5" /> {thread.file.file_number}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" disabled={summarizing} onClick={() => summarize(true)} data-testid="thread-regenerate-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${summarizing ? 'animate-spin' : ''}`} /> Régénérer
                  </Button>
                </div>
              </div>
              {summarizing && !summary?.summary && (
                <p className="text-xs text-violet-700 flex items-center gap-2" data-testid="thread-summary-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> L&apos;IA lit les {thread.messages.length} message(s) et rédige le résumé…</p>
              )}
              {summary?.summary && (
                <>
                  <SummaryText text={summary.summary} />
                  <p className="text-[10px] text-gray-400 mt-2">
                    Généré le {new Date(summary.generated_at).toLocaleString('fr-CA')} · {summary.message_count} message(s)
                    {summarizing && ' · mise à jour en cours…'}
                  </p>
                </>
              )}
            </div>

            <div className="space-y-2" data-testid="thread-messages">
              {thread.messages.map((m) => <ThreadMessage key={m.id} m={m} highlight={m.id === emailId} />)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailThreadDialog;
